import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WorkflowSettingsTab = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [allowSelf, setAllowSelf] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/finance/workflow-config`);
      setRows(r.data.config || []);
      setAllowSelf(!!r.data.allow_self_approval);
    } catch {
      toast.error('Failed to load workflow settings');
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (subcategory, patch, current) => {
    try {
      await axios.put(`${API_URL}/api/finance/workflow-config/${subcategory}`, { ...current, ...patch });
      toast.success('Workflow setting updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Only Super Admin can change workflow settings');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base">Separation of Duties</CardTitle>
          <CardDescription>When off, users can never approve their own requisitions or withdrawals</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Switch checked={allowSelf} disabled={!isSuperAdmin}
            onCheckedChange={(c) => update('_global', { requires_requisition: false, allow_self_approval: c }, {})}
            data-testid="allow-self-approval-switch" />
          <span className="text-sm">{allowSelf ? 'Self-approval allowed' : 'Self-approval blocked (recommended)'}</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base">Per Expense Type Requirements</CardTitle>
          <CardDescription>Configure which expense types require a requisition/approval and supporting documents{!isSuperAdmin && ' (Super Admin only)'}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expense Type</TableHead>
                <TableHead>Requires Requisition</TableHead>
                <TableHead>Requires Documents</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.subcategory}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell>
                    <Switch checked={r.requires_requisition} disabled={!isSuperAdmin}
                      onCheckedChange={(c) => update(r.subcategory, { requires_requisition: c }, { requires_documents: r.requires_documents })}
                      data-testid={`wf-req-${r.subcategory}`} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={r.requires_documents} disabled={!isSuperAdmin}
                      onCheckedChange={(c) => update(r.subcategory, { requires_documents: c }, { requires_requisition: r.requires_requisition })}
                      data-testid={`wf-doc-${r.subcategory}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
